import { icons } from '../assets/icons';
import { ReleaseInfo, PROJECT_CONFIG, FALLBACK_RELEASE, FALLBACK_PRERELEASE } from '../config/project-info';

export class HeroComponent {
  private stableRelease: ReleaseInfo = FALLBACK_RELEASE;
  private prerelease: ReleaseInfo | null = FALLBACK_PRERELEASE;
  private onOpenReleaseModal: (release?: ReleaseInfo) => void;

  constructor(
    stableRelease: ReleaseInfo, 
    prerelease: ReleaseInfo | null, 
    onOpenReleaseModal: (release?: ReleaseInfo) => void
  ) {
    this.stableRelease = stableRelease;
    this.prerelease = prerelease;
    this.onOpenReleaseModal = onOpenReleaseModal;
  }

  public updateReleases(stable: ReleaseInfo, prerelease: ReleaseInfo | null) {
    this.stableRelease = stable;
    this.prerelease = prerelease;

    const versionEl = document.getElementById('hero-version-text');
    if (versionEl) versionEl.textContent = stable.version;

    const dlBtnText = document.getElementById('hero-main-download-text');
    if (dlBtnText) {
      dlBtnText.textContent = `Get ${stable.version}`;
    }

    const preContainer = document.getElementById('hero-prerelease-banner');
    const preVersionEl = document.getElementById('hero-prerelease-version-text');
    const preCtaBtn = document.getElementById('hero-prerelease-cta-btn');

    if (preContainer) {
      if (this.prerelease) {
        preContainer.classList.remove('hidden');
        if (preVersionEl) preVersionEl.textContent = this.prerelease.version;
      } else {
        preContainer.classList.add('hidden');
      }
    }

    if (preCtaBtn) {
      if (this.prerelease) {
        preCtaBtn.classList.remove('hidden');
        const preCtaText = document.getElementById('hero-prerelease-cta-text');
        if (preCtaText) preCtaText.textContent = `Preview ${this.prerelease.version}`;
      } else {
        preCtaBtn.classList.add('hidden');
      }
    }
  }

  public render(): string {
    return `
      <section class="relative pt-10 pb-16 md:pt-16 md:pb-24 overflow-hidden">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          
          <div class="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            
            <!-- Left Column: Copy & CTAs (7 cols) -->
            <div class="lg:col-span-7 space-y-6 text-left">
              
              <!-- Release Version Badges -->
              <div class="flex flex-wrap items-center gap-2.5">
                
                <!-- Stable Version Pill -->
                <div class="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-terminal-card border border-matrix-500/40 text-xs font-mono shadow-glow-sm">
                  <span class="flex h-2 w-2 relative">
                    <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-matrix-400 opacity-75"></span>
                    <span class="relative inline-flex rounded-full h-2 w-2 bg-matrix-500"></span>
                  </span>
                  <span class="text-terminal-muted">Latest release:</span>
                  <strong id="hero-version-text" class="text-matrix-400 font-semibold">${this.stableRelease.version}</strong>
                  <span class="text-matrix-800">|</span>
                  <button 
                    id="hero-changelog-btn" 
                    class="text-cyber-400 hover:text-cyber-300 hover:underline flex items-center gap-0.5 font-semibold cursor-pointer"
                  >
                    <span>Release Notes</span>
                    ${icons.chevronRight('w-3.5 h-3.5')}
                  </button>
                </div>

                <!-- Pre-Release Pill (If active) -->
                <div id="hero-prerelease-banner" class="${this.prerelease ? '' : 'hidden'} inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-950/70 border border-amber-500/40 text-xs font-mono">
                  ${icons.flask('w-3.5 h-3.5 text-amber-400')}
                  <span class="text-amber-300 font-medium">Pre-release:</span>
                  <strong id="hero-prerelease-version-text" class="text-amber-100 font-semibold">${this.prerelease ? this.prerelease.version : ''}</strong>
                  <span class="text-amber-700">|</span>
                  <button 
                    id="hero-prerelease-changelog-btn" 
                    class="text-amber-300 hover:underline flex items-center gap-0.5 font-semibold cursor-pointer"
                  >
                    <span>Inspect</span>
                    ${icons.chevronRight('w-3.5 h-3.5')}
                  </button>
                </div>

              </div>

              <!-- Main Title -->
              <h1 class="text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.15]">
                A <span class="text-matrix-500 text-glow">retro‑modern</span> Mastodon client for your terminal
              </h1>

              <!-- Subtitle -->
              <p class="text-base sm:text-lg text-terminal-muted leading-relaxed max-w-2xl font-mono">
                Blazing‑fast TUI built with Python and Textual. Multi-account, multi‑column timelines, high-fidelity image rendering (ANSI/Sixel/TGP), persistent SQLite cache, and an elegant keyboard‑driven workflow.
              </p>

              <!-- Primary Action Buttons -->
              <div class="flex flex-wrap items-center gap-3 pt-2">
                
                <!-- Quick Install Button (Scrolls to Install) -->
                <a 
                  href="#install"
                  class="inline-flex items-center gap-2 px-5 py-3 rounded-xl font-mono font-bold text-sm text-terminal-bgDark bg-matrix-500 hover:bg-matrix-400 shadow-glow hover:shadow-glow-lg transition-all"
                >
                  <span>➜ Quick Install</span>
                </a>

                <!-- Download Binaries / Release Modal Button -->
                <button 
                  id="hero-modal-btn"
                  class="inline-flex items-center gap-2 px-5 py-3 rounded-xl font-mono font-semibold text-sm text-cyber-300 bg-terminal-card hover:bg-terminal-cardLight border border-cyber-500/50 hover:border-cyber-400 shadow-glow-cyan transition-all cursor-pointer"
                >
                  ${icons.download('w-4 h-4 text-cyber-400')}
                  <span id="hero-main-download-text">Get ${this.stableRelease.version}</span>
                </button>

                <!-- Documentation Button -->
                <a 
                  href="#/docs"
                  class="inline-flex items-center gap-2 px-4 py-3 rounded-xl font-mono font-medium text-sm text-terminal-fg bg-terminal-card hover:bg-terminal-cardLight border border-terminal-border hover:border-matrix-500 transition-all"
                >
                  ${icons.bookOpen('w-4 h-4 text-matrix-400')}
                  <span>Docs</span>
                </a>

                <!-- Follow Mastodon Button -->
                <a 
                  href="${PROJECT_CONFIG.mastodonUrl}"
                  target="_blank"
                  rel="me noopener noreferrer"
                  class="inline-flex items-center gap-2 px-4 py-3 rounded-xl font-mono font-medium text-sm text-cyber-400 hover:text-cyber-300 bg-cyber-950/40 hover:bg-cyber-950/70 border border-cyber-800/60 hover:border-cyber-600 transition-all"
                >
                  ${icons.mastodon('w-4 h-4 text-cyber-400')}
                  <span>Follow @Mastui</span>
                </a>

              </div>

              <!-- Quick Terminal Copy Bar -->
              <div class="pt-3">
                <div class="flex items-center justify-between gap-3 p-3 rounded-xl bg-terminal-bgDark border border-matrix-900 shadow-inner font-mono text-xs sm:text-sm">
                  <div class="flex items-center gap-2 overflow-x-auto whitespace-nowrap text-terminal-fg">
                    <span class="text-matrix-500 select-none">$</span>
                    <span class="text-matrix-300">pipx install mastui && mastui</span>
                  </div>
                  <button 
                    id="hero-copy-cmd-btn"
                    class="p-1.5 rounded-lg text-matrix-600 hover:text-matrix-300 hover:bg-terminal-card transition-colors flex-shrink-0 cursor-pointer"
                    title="Copy command to clipboard"
                  >
                    <span id="hero-copy-icon">${icons.copy('w-4 h-4')}</span>
                    <span id="hero-copied-icon" class="hidden text-matrix-400">${icons.check('w-4 h-4')}</span>
                  </button>
                </div>
              </div>

              <!-- Feature Highlights Row -->
              <div class="flex flex-wrap items-center gap-x-5 gap-y-2 pt-2 text-xs font-mono text-terminal-muted">
                <div class="flex items-center gap-1.5">
                  <span class="text-matrix-500">✓</span>
                  <span>Python 3.9 - 3.14</span>
                </div>
                <div class="flex items-center gap-1.5">
                  <span class="text-matrix-500">✓</span>
                  <span>ANSI / Sixel / TGP Media</span>
                </div>
                <div class="flex items-center gap-1.5">
                  <span class="text-matrix-500">✓</span>
                  <span>Multi-Account Sandbox</span>
                </div>
                <div class="flex items-center gap-1.5">
                  <span class="text-matrix-500">✓</span>
                  <span>100% Open Source (MIT)</span>
                </div>
              </div>

            </div>

            <!-- Right Column: Interactive Terminal Preview Window (5 cols) -->
            <div class="lg:col-span-5">
              <div class="rounded-2xl border border-terminal-border bg-terminal-card shadow-glow-lg overflow-hidden relative">
                
                <!-- Terminal Window Chrome -->
                <div class="flex items-center justify-between px-4 py-3 bg-terminal-bgDark/90 border-b border-terminal-border">
                  <div class="flex items-center gap-2">
                    <div class="w-3 h-3 rounded-full bg-rose-500/80"></div>
                    <div class="w-3 h-3 rounded-full bg-amber-500/80"></div>
                    <div class="w-3 h-3 rounded-full bg-matrix-500/80"></div>
                    <span class="ml-2 text-xs font-mono text-terminal-muted">kim@tty0:~ (mastui)</span>
                  </div>
                  <div class="flex items-center gap-1 text-[11px] font-mono text-matrix-400">
                    <span class="w-2 h-2 rounded-full bg-matrix-500 animate-pulse"></span>
                    <span>LIVE TUI</span>
                  </div>
                </div>

                <!-- Terminal Body Content -->
                <div class="p-4 space-y-3 bg-terminal-bgDark text-left font-mono text-xs leading-relaxed">
                  <div class="text-terminal-muted">
                    <span class="text-matrix-500">kim@tty0:~$</span> pipx install mastui<br />
                    <span class="text-terminal-muted/70">  installed package mastui 1.12.0, installed python 3.12</span><br />
                    <span class="text-matrix-500">kim@tty0:~$</span> mastui
                  </div>

                  <!-- Screenshot Preview -->
                  <div class="rounded-xl overflow-hidden border border-matrix-900/80 shadow-md">
                    <img 
                      src="/assets/screenshot.png" 
                      alt="Mastui Terminal Mastodon client screenshot" 
                      class="w-full h-auto object-cover hover:scale-[1.02] transition-transform duration-300 cursor-pointer"
                      id="hero-preview-image"
                    />
                  </div>

                  <!-- Status Bar simulation -->
                  <div class="flex items-center justify-between pt-1 text-[11px] text-matrix-600 border-t border-matrix-950">
                    <span>[H] Home  [N] Notifs  [M] Mentions</span>
                    <span>Press '?' for help</span>
                  </div>
                </div>

              </div>
            </div>

          </div>

        </div>
      </section>
    `;
  }

  public bindEvents() {
    const changelogBtn = document.getElementById('hero-changelog-btn');
    if (changelogBtn) {
      changelogBtn.addEventListener('click', () => {
        this.onOpenReleaseModal(this.stableRelease);
      });
    }

    const preChangelogBtn = document.getElementById('hero-prerelease-changelog-btn');
    if (preChangelogBtn) {
      preChangelogBtn.addEventListener('click', () => {
        if (this.prerelease) {
          this.onOpenReleaseModal(this.prerelease);
        }
      });
    }

    const modalBtn = document.getElementById('hero-modal-btn');
    if (modalBtn) {
      modalBtn.addEventListener('click', () => {
        this.onOpenReleaseModal(this.stableRelease);
      });
    }

    const heroImg = document.getElementById('hero-preview-image');
    if (heroImg) {
      heroImg.addEventListener('click', () => {
        const galleryEl = document.getElementById('gallery');
        if (galleryEl) {
          galleryEl.scrollIntoView({ behavior: 'smooth' });
        }
      });
    }

    const copyBtn = document.getElementById('hero-copy-cmd-btn');
    const copyIcon = document.getElementById('hero-copy-icon');
    const copiedIcon = document.getElementById('hero-copied-icon');

    if (copyBtn) {
      copyBtn.addEventListener('click', async () => {
        const cmd = 'pipx install mastui && mastui';
        try {
          await navigator.clipboard.writeText(cmd);
          if (copyIcon && copiedIcon) {
            copyIcon.classList.add('hidden');
            copiedIcon.classList.remove('hidden');
            setTimeout(() => {
              copyIcon.classList.remove('hidden');
              copiedIcon.classList.add('hidden');
            }, 2000);
          }
        } catch {
          // ignore
        }
      });
    }
  }
}
